import {Dispatch, ReactNode, SetStateAction, useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {caronaApi, SUGESTOES_ALTERNATIVAS_TIMEOUT_MS} from '../../../services/CaronaApi.service'
import {
    AlternativaConsiderada,
    AtaDetalhe,
    AtaResumo,
    DocumentoProcessoOut,
    EsferaCodigo,
    ItemComparado,
    PesquisaVinculadaOut,
    RespostasEntrevista,
    SugestaoAlternativa,
} from '../types'
import {avaliarConformidade, ReqStatus, resumoConformidade} from '../compliance'
import {ESFERA_LABEL, fmtDate, itemAderenteLocal} from '../ui'

interface Props {
    ata: AtaDetalhe
    termoBuscado: string
    aderenteNome: string
    respostas: RespostasEntrevista
    setRespostas: Dispatch<SetStateAction<RespostasEntrevista>>
    evidencias: Record<string, string>
    setEvidencias: Dispatch<SetStateAction<Record<string, string>>>
    gerando: boolean
    onGerar: () => void
    sugerindo: boolean
    onSugerirDemanda: () => void
    onVoltar: () => void
    /** Handoff ao módulo de Pesquisa de Preços (IN 65) pré-preenchido com o item/
     * objeto da ata; a pesquisa criada volta vinculada à justificativa. Opcional
     * para não quebrar quem renderiza a Entrevista sem o fluxo (ex.: testes). */
    onPesquisarPrecos?: () => void
    /** Pesquisa já vinculada à justificativa desta ata (status/valores). */
    pesquisaVinculada?: PesquisaVinculadaOut | null
    /** Outras atas do mesmo objeto que a busca trouxe — as alternativas a comparar
     * na seção do ETP (Acórdão 547/2026-TCU). Opcional (testes/histórico). */
    alternativas?: AtaResumo[]
    /** Troca a ata ESCOLHIDA por uma alternativa (o gestor decidiu adotar a mais
     * vantajosa ANTES de gerar o relatório): o CaronaManager carrega o detalhe da
     * nova ata preservando as respostas/demanda. Opcional (testes/histórico). */
    onAdotarAta?: (numeroControle: string) => void
    /** Abre o DETALHE de uma ata (fluxo abrirDetalhe do CaronaManager) — fallback
     * do "conferir item" quando a alternativa não tem link_pncp. Opcional (testes). */
    onAbrirDetalhe?: (numeroControle: string) => void
    // Documento de planejamento (ETP/DFD/TR) como contexto da IA — opcionais para não
    // quebrar quem renderiza a Entrevista sem o fluxo de upload (ex.: testes).
    documento?: DocumentoProcessoOut | null
    documentosDisponiveis?: DocumentoProcessoOut[]
    enviandoDocumento?: boolean
    erroDocumento?: string | null
    onEnviarDocumento?: (file: File) => void
    onSelecionarDocumento?: (id: string) => void
    onRemoverDocumento?: () => void
}

/** Rótulo amigável do tipo detectado pelo backend (heurística sobre o texto). */
const TIPO_DOC_LABEL: Record<string, string> = {
    ETP: 'Estudo Técnico Preliminar',
    DFD: 'Documento de Formalização da Demanda',
    TR: 'Termo de Referência',
    OUTRO: 'Documento de planejamento',
}

/** Prefixa "Ata " ao número só quando ele ainda não começa com "Ata" — o PNCP às
 * vezes já traz "Ata nº 00002/2026", e concatenar "Ata " cegamente gerava o rótulo
 * duplicado "Ata Ata nº 00002/2026". */
function rotuloNumeroAta(numero: string): string {
    const n = (numero || '').trim()
    return /^ata\b/i.test(n) ? n : `Ata ${n}`
}

/** Rótulo canônico de uma alternativa (ata) na análise do ETP: "Ata nº X — Órgão
 * (UF)", sem duplicar o prefixo "Ata". É também a CHAVE textual que casa a linha
 * com a sugestão da Licito.guru IA (justificativa_sugerida por ata). */
function rotuloAlternativa(a: AtaResumo): string {
    return `${rotuloNumeroAta(a.numero_ata || a.numero_controle_pncp)} — ${a.orgao}${a.uf ? ` (${a.uf})` : ''}`
}

/** Formata em reais pt-BR (mesma convenção do quadro do ETP na JustificativaView). */
function brl(v: number): string {
    return v.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})
}

/** Rótulo do valor UNITÁRIO do item exato cotejado ("R$ X/UN"). "" quando não há
 * preço (o quadro degrada para "sem preço comparável"). */
function valorUnitarioTexto(it: ItemComparado | null | undefined): string {
    if (!it || it.valor_unitario == null) return ''
    const un = (it.unidade || '').trim() ? `/${it.unidade}` : ''
    return `${brl(it.valor_unitario)}${un}`
}

type SeloCmp = 'barata' | 'cara' | 'igual' | 'incomparavel'

/** Compara o preço UNITÁRIO NORMALIZADO (R$ por unidade_base) de uma alternativa
 * com o da ata escolhida — só na MESMA unidade-base (comparação justa; item-a-item).
 * Espelha o sinal determinístico do backend (Acórdão 547/2026-TCU, § 73): a decisão é
 * do gestor, este selo é só apoio. `incomparavel` = falta preço ou base diferente. */
function compararUnitario(
    alt: ItemComparado | null | undefined,
    esc: ItemComparado | null | undefined
): SeloCmp {
    if (!alt || !esc) return 'incomparavel'
    const a = alt.valor_unitario_normalizado
    const e = esc.valor_unitario_normalizado
    if (a == null || e == null) return 'incomparavel'
    if ((alt.unidade_base || '') !== (esc.unidade_base || '')) return 'incomparavel'
    if (a < e) return 'barata'
    if (a > e) return 'cara'
    return 'igual'
}

const TIMEOUT_SEGUNDOS = Math.round(SUGESTOES_ALTERNATIVAS_TIMEOUT_MS / 1000)

/** O erro foi ESTOURO DE TEMPO (a chamada não voltou dentro do timeout)? O axios
 * sinaliza com code ECONNABORTED/ETIMEDOUT e sem `response`. Distinguir importa: a
 * mensagem para o gestor é outra ("demorou demais, tente de novo"), e não um erro
 * genérico de serviço. */
function ehEstouroDeTempo(err: any): boolean {
    if (err?.response) return false
    const code = err?.code
    return code === 'ECONNABORTED' || code === 'ETIMEDOUT' || /timeout/i.test(err?.message || '')
}

/** Segundos decorridos desde que `ativo` virou true (volta a 0 quando termina).
 * Alimenta o indicador de progresso das chamadas longas (até ~26s no backend). */
function useSegundosDecorridos(ativo: boolean): number {
    const [segundos, setSegundos] = useState(0)
    useEffect(() => {
        setSegundos(0)
        if (!ativo) return
        const id = setInterval(() => setSegundos((s) => s + 1), 1000)
        return () => clearInterval(id)
    }, [ativo])
    return segundos
}

/** Indicador de PROGRESSO das chamadas on-demand da IA: barra determinística
 * (fração do teto de ~26s do backend, travada em 95% até a resposta chegar) +
 * segundos decorridos. Sem isso o gestor não sabe se a tela travou. */
function ProgressoIA({segundos, rotulo}: {segundos: number; rotulo: string}) {
    const pct = Math.min(95, Math.round((segundos / TIMEOUT_SEGUNDOS) * 100))
    return (
        <div style={{display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 0'}}>
            <div
                role="progressbar"
                aria-label={rotulo}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                style={{flex: 1, maxWidth: 260, height: 6, borderRadius: 3, background: 'var(--border-subtle)', overflow: 'hidden'}}
            >
                <div style={{width: `${pct}%`, height: '100%', background: 'var(--brand-primary)', transition: 'width .4s linear'}} />
            </div>
            <span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                {rotulo} — {segundos}s de até {TIMEOUT_SEGUNDOS}s
            </span>
        </div>
    )
}

const REQ_ICON: Record<ReqStatus, string> = {
    ok: '<path d="M20 6 9 17l-5-5"/>',
    risco: '<line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
    pendente: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
    lembrete: '<line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
    na: '<line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>',
}

function autoCheck(ok: boolean, okMsg: string, failMsg: string) {
    return (
        <div className={`callout ${ok ? 'callout--success' : 'callout--danger'}`} style={{margin: 0}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                dangerouslySetInnerHTML={{__html: ok ? '<path d="M20 6 9 17l-5-5"/>' : '<circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>'}} />
            <div>{ok ? okMsg : failMsg}</div>
        </div>
    )
}

function Bloco({n, title, req, children}: {n: number; title: string; req: string; children: ReactNode}) {
    return (
        <div className="bloco">
            <div className="bloco__head">
                <span className="bloco__num">{n}</span>
                <div><div className="bloco__title">{title}</div><div className="bloco__req">{req}</div></div>
            </div>
            <div className="bloco__body">{children}</div>
        </div>
    )
}

export default function Entrevista(
    {ata, termoBuscado, aderenteNome, respostas, setRespostas, evidencias, setEvidencias, gerando, onGerar, sugerindo, onSugerirDemanda, onVoltar,
     onPesquisarPrecos, pesquisaVinculada = null, alternativas = [], onAdotarAta, onAbrirDetalhe,
     documento = null, documentosDisponiveis = [], enviandoDocumento = false, erroDocumento = null,
     onEnviarDocumento, onSelecionarDocumento, onRemoverDocumento}: Props
) {
    const e = respostas
    const aderente = e.esfera_aderente
    // Merge sempre no estado MAIS RECENTE (forma funcional): callbacks assíncronos
    // — ex.: a sugestão de IA da demanda — não podem sobrescrever toggles que o
    // usuário marcou enquanto a IA gerava (era o bug do "precisa apertar de novo").
    const set = (patch: Partial<RespostasEntrevista>) => setRespostas((prev) => ({...prev, ...patch}))

    const toggleEv = (id: string, nome: string) =>
        setEvidencias((prev) => ({...prev, [id]: prev[id] ? '' : nome}))
    const setQtd = (item: number, v: number) =>
        set({quantidades_desejadas: {...e.quantidades_desejadas, [String(item)]: v}})

    // Campo supervisionado do gestor: alternativas consideradas + razões (Acórdão
    // 547/2026-TCU). Persistido em respostas.* e enviado à IA como matéria-prima.
    const consideradas: AlternativaConsiderada[] = e.alternativas_consideradas || []
    const setAlt = (rows: AlternativaConsiderada[]) => set({alternativas_consideradas: rows})
    const addAlt = () => setAlt([...consideradas, {referencia: '', motivo: ''}])
    const updateAlt = (i: number, patch: Partial<AlternativaConsiderada>) =>
        setAlt(consideradas.map((r, idx) => (idx === i ? {...r, ...patch} : r)))
    const removeAlt = (i: number) => setAlt(consideradas.filter((_, idx) => idx !== i))
    // Preenche as linhas com as atas alternativas em mãos (+ a licitação própria) para
    // o gestor apenas anotar o porquê de cada uma não ter sido a escolhida.
    const importarAlternativas = () => {
        const dasAtas: AlternativaConsiderada[] = (alternativas || []).slice(0, 6).map((a) => ({
            referencia: rotuloAlternativa(a),
            motivo: '',
        }))
        setAlt([...dasAtas, {referencia: 'Realização de licitação própria pelo órgão', motivo: ''}])
    }

    // Sugestões ON-DEMAND da Licito.guru IA para o quadro do ETP: o botão chama o
    // endpoint (síncrono, até ~18s) com a ata escolhida + os numero_controle_pncp das
    // alternativas exibidas; a resposta é casada por numero_controle → rótulo → linha.
    const [sugerindoAlt, setSugerindoAlt] = useState(false)
    // Mensagem amigável do último erro das sugestões (inclui o estouro de tempo) —
    // fica na tela, não só no toast que desaparece.
    const [erroSugestoes, setErroSugestoes] = useState<string | null>(null)
    const segundosSugestoes = useSegundosDecorridos(sugerindoAlt)

    /** Aplica as sugestões recebidas às linhas do quadro. Supervisão do gestor:
     * preenche só campos VAZIOS; se fosse sobrescrever algo já digitado, confirma
     * antes. Os campos seguem editáveis. A sugestão da escolhida vai para a
     * observação ("por que ela é mais vantajosa"). */
    const aplicarSugestoes = (sugestoesPorRotulo: Map<string, string>, sugestaoEscolhida: string) => {
        // Base: as linhas já existentes ou, se vazias, importa as atas em mãos
        // (+ licitação própria) — mesmo rótulo, para casar com as sugestões.
        const base: AlternativaConsiderada[] = consideradas.length > 0
            ? consideradas
            : [
                ...(alternativas || []).slice(0, 6).map((a) => ({referencia: rotuloAlternativa(a), motivo: ''})),
                {referencia: 'Realização de licitação própria pelo órgão', motivo: ''},
            ]

        const obsAtual = (e.alternativas_observacao || '').trim()
        // Alguma sugestão sobrescreveria conteúdo já digitado pelo gestor (motivo de
        // linha OU observação)? Se sim, pede confirmação antes de substituir.
        const sobrescreveria =
            base.some((r) => {
                const s = sugestoesPorRotulo.get(r.referencia.trim())
                return Boolean(s) && Boolean(r.motivo.trim()) && r.motivo.trim() !== s
            }) ||
            Boolean(sugestaoEscolhida && obsAtual && obsAtual !== sugestaoEscolhida)

        const overwrite = sobrescreveria
            ? window.confirm(
                'Você já preencheu algumas justificativas. Substituir pelas sugestões da Licito.Guru IA?\n\n' +
                'OK: substitui pelas sugestões.\nCancelar: preenche apenas os campos ainda vazios.'
            )
            : false

        const linhas = base.map((r) => {
            const s = sugestoesPorRotulo.get(r.referencia.trim())
            if (!s) return r
            return !r.motivo.trim() || overwrite ? {...r, motivo: s} : r
        })
        const observacao = sugestaoEscolhida && (!obsAtual || overwrite)
            ? sugestaoEscolhida
            : (e.alternativas_observacao || '')

        set({alternativas_consideradas: linhas, alternativas_observacao: observacao})
    }

    /** Chama o endpoint de sugestões (ON-DEMAND, síncrono) e casa cada item por
     * numero_controle de volta à linha do quadro, via o rótulo canônico que a
     * importação usa. `eh_escolhida` → observação. Fail-closed: sugestão "" → em
     * branco. Enquanto a chamada está em voo o botão fica desabilitado. */
    const sugerirJustificativas = async () => {
        if (sugerindoAlt) return
        setSugerindoAlt(true)
        setErroSugestoes(null)
        try {
            // numero_controle das alternativas EXIBIDAS (o backend re-coleta preço).
            const controles = (alternativas || [])
                .map((a) => a.numero_controle_pncp)
                .filter((c): c is string => Boolean(c))
            const itens = await caronaApi.sugestoesAlternativas({
                ata_numero_controle: ata.numero_controle_pncp,
                ...(controles.length ? {alternativas: controles} : {}),
            })

            // Ponte numero_controle → rótulo canônico (mesma montagem das linhas).
            const rotuloPorControle = new Map<string, string>()
            for (const a of alternativas || []) rotuloPorControle.set(a.numero_controle_pncp, rotuloAlternativa(a))

            // rótulo → justificativa (não-escolhidas com texto) + a da escolhida.
            const sugestoesPorRotulo = new Map<string, string>()
            let sugestaoEscolhida = ''
            for (const item of itens) {
                const texto = (item.justificativa_sugerida || '').trim()
                if (!texto) continue // fail-closed: IA fora → deixa o campo em branco
                if (item.eh_escolhida) {
                    sugestaoEscolhida = texto
                    continue
                }
                const rot = rotuloPorControle.get(item.numero_controle)
                if (rot) sugestoesPorRotulo.set(rot, texto)
            }

            if (sugestoesPorRotulo.size === 0 && !sugestaoEscolhida) {
                toast.info('A Licito.Guru IA não retornou sugestões para estas alternativas no momento.')
                return
            }
            aplicarSugestoes(sugestoesPorRotulo, sugestaoEscolhida)
        } catch (err: any) {
            const status = err?.response?.status
            const msg = ehEstouroDeTempo(err)
                ? `As sugestões da Licito.Guru IA passaram de ${TIMEOUT_SEGUNDOS} segundos e a espera foi encerrada. ` +
                  'Nada foi cobrado nem alterado no quadro — clique novamente para tentar de novo.'
                : status === 404
                ? 'Não foi possível gerar sugestões: a ata escolhida não está mais disponível — refaça a busca.'
                : 'Não foi possível gerar as sugestões da Licito.Guru IA agora. Tente novamente em instantes.'
            setErroSugestoes(msg)
            toast.error(msg)
        } finally {
            setSugerindoAlt(false)
        }
    }

    // ---- Comparação de vantajosidade ANTES do relatório (Bloco 7) ----------
    // "USUÁRIO NO COMANDO": o gestor SELECIONA quais das outras atas comparar e vê,
    // de cada uma, o ITEM cotejado + o preço UNITÁRIO, com selo "Mais barata/cara"
    // frente à escolhida — e pode ADOTAR a mais vantajosa aqui, sem esperar o
    // relatório. Reusa o MESMO endpoint on-demand da "Sugerir justificativas"
    // (item_comparado normalizado por unidade-base — comparação item-a-item justa).
    const alternativasExibidas = (alternativas || []).slice(0, 6)
    const controlesExibidos = alternativasExibidas
        .map((a) => a.numero_controle_pncp)
        .filter((c): c is string => Boolean(c))
    const altKey = controlesExibidos.join('|')

    const [selecionadas, setSelecionadas] = useState<Set<string>>(() => new Set(controlesExibidos))
    const [comparacao, setComparacao] = useState<SugestaoAlternativa[] | null>(null)
    const [comparando, setComparando] = useState(false)
    // Mensagem amigável do último erro da comparação (inclui o estouro de tempo).
    const [erroComparacao, setErroComparacao] = useState<string | null>(null)
    const segundosComparacao = useSegundosDecorridos(comparando)
    // Chave (controles ordenados) do que foi efetivamente comparado — detecta que a
    // seleção mudou desde a última comparação (mostra "atualize a comparação").
    const [comparadoKey, setComparadoKey] = useState('')
    // A comparação foi ANCORADA na busca original (termo enviado)? Só então
    // item_comparado=null significa "não localizado nesta ata" (contrato do
    // backend) — sem termo, null mantém o rótulo antigo "sem preço comparável".
    const [comparadoComTermo, setComparadoComTermo] = useState(false)

    // link_pncp por numero_controle (das alternativas em mãos) — alvo do
    // "conferir item" clicável nos cards da comparação.
    const linkPncpPorControle = new Map<string, string>()
    for (const a of alternativasExibidas) {
        if (a.link_pncp) linkPncpPorControle.set(a.numero_controle_pncp, a.link_pncp)
    }

    // Troca de ata escolhida OU novo conjunto de alternativas (ex.: após adotar):
    // reseta a seleção para todas as exibidas e descarta a comparação obsoleta.
    useEffect(() => {
        setSelecionadas(new Set(controlesExibidos))
        setComparacao(null)
        setComparadoKey('')
        setComparadoComTermo(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [altKey, ata.numero_controle_pncp])

    const toggleSelecionada = (controle: string) =>
        setSelecionadas((prev) => {
            const next = new Set(prev)
            if (next.has(controle)) next.delete(controle)
            else next.add(controle)
            return next
        })

    /** Chama o endpoint on-demand com a ata escolhida + as alternativas SELECIONADAS
     * e guarda a resposta (a escolhida + cada alternativa, com item_comparado). O
     * cálculo de vantajosidade é do backend (piso determinístico); a tela só exibe. */
    const compararSelecionadas = async () => {
        if (comparando) return
        const controles = controlesExibidos.filter((c) => selecionadas.has(c))
        if (controles.length === 0) {
            toast.info('Selecione ao menos uma outra ata para comparar.')
            return
        }
        setComparando(true)
        setErroComparacao(null)
        // Âncora do casamento: a BUSCA ORIGINAL do usuário (termoBuscado). Com ela,
        // o backend só devolve item_comparado do item que CASA com a busca —
        // alternativa sem o item volta null ("não localizada"), nunca item alheio.
        const termoAncora = (termoBuscado || '').trim()
        try {
            const itens = await caronaApi.sugestoesAlternativas({
                ata_numero_controle: ata.numero_controle_pncp,
                alternativas: controles,
                ...(termoAncora ? {termo: termoAncora} : {}),
            })
            setComparacao(itens)
            setComparadoComTermo(Boolean(termoAncora))
            setComparadoKey([...controles].sort().join('|'))
            if (itens.length === 0) {
                toast.info('A Licito.Guru IA não encontrou itens comparáveis para estas atas.')
            }
        } catch (err: any) {
            const status = err?.response?.status
            const msg = ehEstouroDeTempo(err)
                ? `A comparação passou de ${TIMEOUT_SEGUNDOS} segundos e a espera foi encerrada — ` +
                  'nada foi cobrado. Tente novamente; se repetir, compare menos atas de uma vez.'
                : status === 404
                ? 'Não foi possível comparar: a ata escolhida não está mais disponível — refaça a busca.'
                : 'Não foi possível comparar os preços agora. Tente novamente em instantes.'
            setErroComparacao(msg)
            toast.error(msg)
        } finally {
            setComparando(false)
        }
    }

    /** Adota uma alternativa como a nova ata escolhida — decisão do gestor, então
     * CONFIRMA antes (as respostas/demanda são preservadas; as quantidades por item
     * são redefinidas porque os itens mudam). O CaronaManager recarrega o detalhe e a
     * comparação recomputa relativa à nova escolhida (via o effect acima). */
    const adotarAlternativa = (s: SugestaoAlternativa) => {
        if (!onAdotarAta || !s.numero_controle) return
        const ok = window.confirm(
            `Adotar a Ata ${s.numero_ata || s.numero_controle}${s.orgao ? ` (${s.orgao})` : ''} como a ata escolhida?\n\n` +
            'Suas respostas e a demanda serão preservadas; as quantidades por item serão ' +
            'redefinidas (os itens da nova ata podem ser outros). A comparação será ' +
            'recalculada em relação à nova ata escolhida.'
        )
        if (ok) onAdotarAta(s.numero_controle)
    }

    // Derivados da comparação (a escolhida vem com eh_escolhida=true e traz o
    // item_comparado base; as alternativas, o item de cada uma).
    const escolhidaCmp = (comparacao || []).find((s) => s.eh_escolhida) || null
    const escItem = escolhidaCmp?.item_comparado ?? null
    const alternativasCmpSel = (comparacao || [])
        .filter((s) => !s.eh_escolhida && selecionadas.has(s.numero_controle))
    const maisBaratas = alternativasCmpSel
        .filter((s) => compararUnitario(s.item_comparado, escItem) === 'barata')
    // A mais vantajosa (menor preço normalizado) entre as mais baratas — alvo do
    // botão "Adotar" do alerta agregado.
    const maisVantajosa = maisBaratas.length
        ? maisBaratas.reduce((a, b) =>
            (a.item_comparado!.valor_unitario_normalizado ?? Infinity) <=
            (b.item_comparado!.valor_unitario_normalizado ?? Infinity) ? a : b)
        : null
    // A seleção mudou desde a última comparação exibida?
    const selecaoDivergente = comparacao != null &&
        Array.from(selecionadas).filter((c) => controlesExibidos.includes(c)).sort().join('|') !== comparadoKey

    const reqs = avaliarConformidade(ata, respostas, evidencias, pesquisaVinculada)
    const {ok, total, pct} = resumoConformidade(reqs)
    const vedado = aderente === 'F' && ['E', 'M', 'D'].includes(ata.esfera_codigo)
    const vigente = ['VIGENTE', 'A_VENCER'].includes(ata.status_vigencia)

    return (
        <div>
            <header className="page-header">
                <div className="crumb">
                    <a onClick={onVoltar}>Detalhe da ata</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>Entrevista de adesão</span>
                </div>
                <div className="header-row"><h1 className="lg-h2">Entrevista de justificativa</h1></div>
                <p className="lg-text-muted" style={{marginBottom: 0}}>
                    Ata {ata.numero_ata || '—'} · {ata.orgao} · órgão aderente <strong>{ESFERA_LABEL[aderente]}</strong>. Responda os 6 blocos; o painel à direita acompanha cada requisito legal.
                </p>
            </header>

            <div className="page-content" style={{maxWidth: 1180}}>
                <div className="callout callout--info" style={{marginBottom: 22}}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
                    <div><span className="callout__title">A força da justificativa vem da evidência, não da redação.</span> Cada pergunta atende a um requisito legal. Este painel é uma prévia — a verificação completa acontece quando você gera a justificativa.</div>
                </div>

                <div className="interview-grid">
                    <div>
                        <Bloco n={1} title="Identificação e necessidade" req="Requisito 2 — motivação compatível com a demanda real (TCU Ac. 2630/2024)">
                            <div className="lg-field">
                                <label className="lg-label lg-label--required">Esfera do órgão aderente (define a vedação do art. 86, §8º)</label>
                                <select className="lg-select" value={e.esfera_aderente}
                                    onChange={(ev) => set({esfera_aderente: ev.target.value as EsferaCodigo})}>
                                    <option value="F">Federal</option>
                                    <option value="E">Estadual</option>
                                    <option value="M">Municipal</option>
                                    <option value="D">Distrital</option>
                                </select>
                            </div>
                            <div className="lg-field">
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6}}>
                                    <label className="lg-label lg-label--required" style={{marginBottom: 0}}>Demanda concreta do órgão aderente</label>
                                    <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onSugerirDemanda} disabled={sugerindo}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/></svg>
                                        {sugerindo ? 'Sugerindo…' : 'Sugerir com Licito.Guru IA'}
                                    </button>
                                </div>
                                <textarea className="lg-textarea" value={e.demanda_descricao}
                                    onChange={(ev) => set({demanda_descricao: ev.target.value})}
                                    placeholder="Descreva a necessidade e a destinação (ex.: substituição de 180 notebooks em fim de vida útil…)" />
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.demanda_prevista_pca ? ' is-on' : ''}`} onClick={() => set({demanda_prevista_pca: !e.demanda_prevista_pca})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>A demanda está prevista no PCA / planejamento</span>
                            </div>
                            {onEnviarDocumento && (
                                <div className="lg-field" style={{marginTop: 12}}>
                                    <label className="lg-label">Anexe o ETP ou outro documento de planejamento (opcional)</label>
                                    {enviandoDocumento ? (
                                        <div className="callout callout--info" style={{margin: 0}}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                                            <div>Extraindo o texto do documento…</div>
                                        </div>
                                    ) : documento ? (
                                        <div className="callout callout--success" style={{margin: 0, alignItems: 'center'}}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                                            <div style={{flex: 1}}>
                                                <strong>{documento.nome_arquivo}</strong>
                                                <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)'}}>
                                                    Lido como {TIPO_DOC_LABEL[documento.tipo_sugerido] || TIPO_DOC_LABEL.OUTRO}. Será usado na redação da justificativa.
                                                </div>
                                            </div>
                                            {onRemoverDocumento && (
                                                <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onRemoverDocumento}>Remover</button>
                                            )}
                                        </div>
                                    ) : (
                                        <>
                                            <label className="lg-btn lg-btn--secondary lg-btn--sm" style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6}}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                                Escolher arquivo (PDF ou DOCX, até 10MB)
                                                <input
                                                    type="file" accept=".pdf,.docx" style={{display: 'none'}}
                                                    onChange={(ev) => {
                                                        const f = ev.target.files && ev.target.files[0]
                                                        if (f) onEnviarDocumento(f)
                                                        ev.target.value = '' // permite reenviar o mesmo arquivo após erro
                                                    }}
                                                />
                                            </label>
                                            {documentosDisponiveis.length > 0 && onSelecionarDocumento && (
                                                <select
                                                    className="lg-select" style={{marginTop: 8}} value=""
                                                    onChange={(ev) => { if (ev.target.value) onSelecionarDocumento(ev.target.value) }}
                                                >
                                                    <option value="">Ou selecione um documento já enviado…</option>
                                                    {documentosDisponiveis.map((d) => (
                                                        <option key={d.id} value={d.id}>{d.nome_arquivo} ({TIPO_DOC_LABEL[d.tipo_sugerido] || 'Documento'})</option>
                                                    ))}
                                                </select>
                                            )}
                                        </>
                                    )}
                                    {erroDocumento && !enviandoDocumento && (
                                        <div className="callout callout--danger" style={{margin: '8px 0 0'}}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                            <div>{erroDocumento}</div>
                                        </div>
                                    )}
                                    <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', margin: '8px 0 0'}}>
                                        O conteúdo do documento é lido pela Licito.Guru IA e usado apenas como contexto para redigir a
                                        justificativa — nada é copiado automaticamente nem anexado ao documento final.
                                    </p>
                                </div>
                            )}
                            <div className="toggle-row">
                                <div className={`toggle${e.alega_urgencia ? ' is-on' : ''}`} onClick={() => set({alega_urgencia: !e.alega_urgencia})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Há urgência / risco de desabastecimento ou descontinuidade</span>
                            </div>
                            {e.alega_urgencia && (
                                <div className="lg-field">
                                    <label className="lg-label lg-label--required">Evidência da urgência (referência do documento)</label>
                                    <input className="lg-input" type="text" value={evidencias['urgencia_desabastecimento'] || ''}
                                        onChange={(ev) => setEvidencias({...evidencias, urgencia_desabastecimento: ev.target.value})}
                                        placeholder="Ex.: Relatório de estoque 03/2026" />
                                </div>
                            )}
                        </Bloco>

                        <Bloco n={2} title="Vantajosidade da adesão" req="Requisito 1 — o ponto mais sensível (TCU Ac. 420/2018 e 1794/2023)">
                            <div className="callout callout--warn" style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                <div><span className="callout__title">Comparar só com os preços da própria ata é insuficiente.</span>O TCU exige pesquisa de preços própria e atualizada para aferir que o preço registrado é compatível com o mercado.</div>
                            </div>
                            {onPesquisarPrecos && (
                                <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10}}>
                                    <button type="button" className="lg-btn lg-btn--brand lg-btn--sm" onClick={onPesquisarPrecos}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                                        Pesquisar preços (IN 65)
                                    </button>
                                    <span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                                        Abre a pesquisa já preenchida com o item da ata; o resultado volta vinculado à justificativa.
                                    </span>
                                </div>
                            )}
                            {pesquisaVinculada && (
                                <div className={`callout ${pesquisaVinculada.concluida ? 'callout--success' : 'callout--info'}`} style={{margin: 0}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
                                    <div>
                                        {pesquisaVinculada.concluida && pesquisaVinculada.valores ? (
                                            <>Pesquisa de preços vinculada e <strong>concluída</strong>
                                            {pesquisaVinculada.valores.mediana != null && <> — mediana {pesquisaVinculada.valores.mediana.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'})}</>}
                                            . Ao gerar a justificativa, os valores de referência entram como evidência da vantajosidade.</>
                                        ) : pesquisaVinculada.status ? (
                                            <>Pesquisa de preços vinculada, <strong>em andamento</strong> — gere a justificativa novamente após a conclusão para incorporar os valores.</>
                                        ) : (
                                            <>Pesquisa de preços <strong>vinculada</strong> — aguardando dados.</>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="toggle-row">
                                <div className={`toggle${e.declara_preco_compativel ? ' is-on' : ''}`} onClick={() => set({declara_preco_compativel: !e.declara_preco_compativel})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Declaro que o preço registrado é compatível com o parâmetro de mercado</span>
                            </div>
                        </Bloco>

                        <Bloco n={3} title="Limites quantitativos" req="Requisito 5 — 50% individual (§3º) e 200% coletivo (§4º)">
                            <div>
                                {ata.itens.length === 0 && <p style={{fontSize: 'var(--fs-sm)', color: 'var(--text-tertiary)', margin: 0}}>Esta ata não tem itens cadastrados.</p>}
                                {ata.itens.map((it) => {
                                    const limite = Math.floor(it.qtd_registrada * 0.5)
                                    const desejado = e.quantidades_desejadas[String(it.numero_item)] || 0
                                    const excede = desejado > limite
                                    // Item RECOMENDADO para a busca do gestor: sinal do backend
                                    // (?termo= no detalhe) com fallback local (matcher normalizado).
                                    // Apoio VISUAL apenas — nada é bloqueado; pode haver vários.
                                    const recomendado = Boolean(it.aderente) || itemAderenteLocal(termoBuscado, it.descricao)
                                    return (
                                        <div
                                            key={it.numero_item}
                                            style={{
                                                display: 'grid', gridTemplateColumns: '1fr 130px', gap: 12, alignItems: 'center',
                                                padding: '10px 0', borderBottom: '1px solid var(--border-subtle)',
                                                ...(recomendado ? {
                                                    background: 'var(--feedback-info-soft)',
                                                    borderLeft: '3px solid var(--feedback-info)',
                                                    paddingLeft: 10, paddingRight: 6,
                                                } : {}),
                                            }}
                                        >
                                            <div>
                                                <div style={{fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' as any, color: 'var(--text-primary)'}}>
                                                    Item {it.numero_item} — {it.descricao}
                                                    {recomendado && (
                                                        <span
                                                            className="lg-badge lg-badge--info"
                                                            title={`Recomendado para a busca "${(termoBuscado || '').trim()}"`}
                                                            style={{marginLeft: 8, verticalAlign: 'middle'}}
                                                        >
                                                            Recomendado para a sua busca
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{fontSize: 'var(--fs-xs)', color: excede ? 'var(--feedback-danger)' : 'var(--text-tertiary)'}}>
                                                    Registrado: {it.qtd_registrada.toLocaleString('pt-BR')} · limite 50%: {limite.toLocaleString('pt-BR')}{it.valor_unitario != null ? <> · <strong style={{color: 'var(--text-secondary)'}}>{brl(it.valor_unitario)}{(it.unidade || '').trim() ? `/${it.unidade}` : ''}</strong></> : null} {excede ? '· ⚠ excede o teto individual' : ''}
                                                </div>
                                            </div>
                                            <input className="lg-input" type="number" min={0} value={desejado}
                                                onChange={(ev) => setQtd(it.numero_item, parseInt(ev.target.value || '0', 10))}
                                                style={excede ? {borderColor: 'var(--feedback-danger)'} : undefined} />
                                        </div>
                                    )
                                })}
                            </div>
                            <div className={`callout ${ata.saldo_status === 'DISPONIVEL' ? 'callout--info' : 'callout--warn'}`} style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                <div>Saldo coletivo (200%, §4º): <strong>{ata.saldo_status === 'DISPONIVEL' ? 'disponível' : ata.saldo_status === 'ESGOTADO' ? 'esgotado' : 'indeterminado'}</strong>. Confirme com o gerenciador antes de aderir.</div>
                            </div>
                        </Bloco>

                        <Bloco n={4} title="Vigência e esfera" req="Requisitos 6 e 7 — validações automáticas">
                            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                                {autoCheck(vigente, `Ata vigente até ${fmtDate(ata.vigencia_fim)}.`, 'Ata fora de vigência.')}
                                {autoCheck(!vedado, `Compatibilidade de esfera OK: aderente ${ESFERA_LABEL[aderente]} × gerenciador ${ESFERA_LABEL[ata.esfera_codigo] || ata.esfera}.`, `Vedação art. 86, §8º — federal não adere a ata ${(ESFERA_LABEL[ata.esfera_codigo] || '').toLowerCase()}.`)}
                            </div>
                        </Bloco>

                        <Bloco n={5} title="Anuências" req="Requisitos 3 e 4 — gerenciador e fornecedor (a obter)">
                            <div className="callout callout--info" style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                <div>
                                    <span className="callout__title">O que você precisará juntar ao processo</span>
                                    Antes de formalizar a adesão, obtenha: (1) a <strong>autorização do órgão gerenciador</strong> da ata e (2) o <strong>aceite do fornecedor registrado</strong> em fornecer ao órgão não participante. Enquanto não constarem dos autos, a justificativa é uma <strong>minuta condicionada</strong> a essas anuências.
                                </div>
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.ciente_anuencias ? ' is-on' : ''}`} onClick={() => set({ciente_anuencias: !e.ciente_anuencias})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Estou ciente de que preciso obter a autorização do gerenciador e o aceite do fornecedor antes de formalizar a adesão</span>
                            </div>
                        </Bloco>

                        <Bloco n={6} title="Edital de origem" req="Requisito 7 — cláusula de adesão (a confirmar)">
                            <div className="callout callout--info" style={{margin: 0}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                                <div>
                                    <span className="callout__title">Confirme no edital de origem</span>
                                    A adesão por não participantes depende de o edital/ata de origem admiti-la. Rode o <strong>Analisador de Editais</strong> sobre o documento de origem para localizar a cláusula. Enquanto não confirmado, fica como ponto pendente da minuta.
                                </div>
                            </div>
                            <div className="toggle-row">
                                <div className={`toggle${e.ciente_edital ? ' is-on' : ''}`} onClick={() => set({ciente_edital: !e.ciente_edital})}></div>
                                <span style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>Estou ciente de que devo confirmar no edital de origem se há previsão de adesão por não participantes</span>
                            </div>
                        </Bloco>

                        <Bloco n={7} title="Análise de alternativas (para o ETP)" req="Acórdão 547/2026-TCU — a escolha da ata deve estar fundamentada no ETP (art. 18, §1º, V)">
                            <div className="callout callout--info" style={{margin: '0 0 4px'}}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                <div>
                                    <span className="callout__title">Por que esta ata, e não outra (ou uma licitação própria)?</span>
                                    O TCU (Acórdão 547/2026-Plenário) exige que a decisão de aderir e a escolha da ata estejam fundamentadas no seu ETP, comparando as alternativas (objeto, preço, condições e logística). Registre <strong>o que você considerou e por quê</strong> — a Licito.Guru IA usa essas razões como matéria-prima; a análise e a decisão continuam suas.
                                </div>
                            </div>
                            {alternativas.length > 0 && (
                                <div style={{fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)'}}>
                                    <div style={{marginBottom: 6}}>
                                        Outras atas do mesmo objeto encontradas na busca — <strong>selecione as que quer comparar</strong> com a ata escolhida (item exato e preço unitário):
                                    </div>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 4, margin: '0 0 8px'}}>
                                        {alternativasExibidas.map((a) => (
                                            <label
                                                key={a.numero_controle_pncp}
                                                style={{display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer'}}
                                            >
                                                <input
                                                    type="checkbox"
                                                    style={{marginTop: 3}}
                                                    checked={selecionadas.has(a.numero_controle_pncp)}
                                                    onChange={() => toggleSelecionada(a.numero_controle_pncp)}
                                                    aria-label={`Comparar ${rotuloAlternativa(a)}`}
                                                />
                                                <span>
                                                    {rotuloNumeroAta(a.numero_ata || a.numero_controle_pncp)} — {a.orgao}
                                                    {a.uf ? ` (${a.uf})` : ''} · {a.selo_rotulo}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10}}>
                                        <button
                                            type="button"
                                            className="lg-btn lg-btn--brand lg-btn--sm"
                                            onClick={compararSelecionadas}
                                            disabled={comparando || controlesExibidos.filter((c) => selecionadas.has(c)).length === 0}
                                            aria-busy={comparando}
                                            title={`Compara o item exato e o preço unitário das atas selecionadas com a ata escolhida (pode levar até ${TIMEOUT_SEGUNDOS}s).`}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>
                                            {comparando ? 'Comparando preços (Licito.Guru IA)…' : 'Comparar preços das selecionadas'}
                                        </button>
                                        <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={importarAlternativas}>
                                            Preencher com as atas encontradas
                                        </button>
                                    </div>
                                    {comparando && (
                                        <ProgressoIA segundos={segundosComparacao} rotulo="Comparando preços" />
                                    )}
                                    {erroComparacao && !comparando && (
                                        <div className="callout callout--danger" role="alert" style={{margin: '10px 0 0'}}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                            <div>{erroComparacao}</div>
                                        </div>
                                    )}

                                    {comparacao != null && (
                                        <div style={{margin: '12px 0 2px'}}>
                                            {selecaoDivergente && (
                                                <div className="callout callout--info" style={{margin: '0 0 10px'}}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                                    <div>Você mudou a seleção — clique em <strong>Comparar preços das selecionadas</strong> para atualizar o quadro abaixo.</div>
                                                </div>
                                            )}
                                            {maisBaratas.length > 0 && (
                                                <div className="callout callout--warn" role="alert" style={{margin: '0 0 10px'}}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                                    <div style={{flex: 1}}>
                                                        <span className="callout__title">
                                                            Atenção: {maisBaratas.length === 1 ? 'há uma ata mais barata' : `há ${maisBaratas.length} atas mais baratas`} por unidade
                                                        </span>
                                                        Considere adotar a alternativa mais vantajosa — a decisão é sua; os demais critérios do § 73 (condições, logística, prazos) também pesam.
                                                        {maisVantajosa && onAdotarAta && (
                                                            <div style={{marginTop: 8}}>
                                                                <button
                                                                    type="button"
                                                                    className="lg-btn lg-btn--brand lg-btn--sm"
                                                                    onClick={() => adotarAlternativa(maisVantajosa)}
                                                                >
                                                                    Adotar a mais vantajosa (Ata {maisVantajosa.numero_ata || maisVantajosa.numero_controle})
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {escItem && (
                                                <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', marginBottom: 6}}>
                                                    Ata escolhida ({rotuloNumeroAta(ata.numero_ata || ata.numero_controle_pncp)}):{' '}
                                                    <strong>{valorUnitarioTexto(escItem) || 'sem preço comparável'}</strong>
                                                    {escItem.descricao ? ` · ${escItem.descricao}` : ''}
                                                </div>
                                            )}
                                            {alternativasCmpSel.length === 0 ? (
                                                <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                                                    Nenhuma ata selecionada na comparação.
                                                </div>
                                            ) : (
                                                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                                                    {alternativasCmpSel.map((s) => {
                                                        const it = s.item_comparado
                                                        // Comparação ancorada na busca + item null = o backend NÃO
                                                        // localizou o item correspondente nesta ata (contrato: nunca
                                                        // devolve item alheio). Card neutro: sem preço, sem selo,
                                                        // sem "Adotar".
                                                        const naoLocalizado = comparadoComTermo && it == null
                                                        const cmp = naoLocalizado ? 'incomparavel' : compararUnitario(it, escItem)
                                                        const valor = valorUnitarioTexto(it)
                                                        const selo =
                                                            cmp === 'barata' ? {txt: 'Mais barata', cls: 'callout--danger'} :
                                                            cmp === 'cara' ? {txt: 'Mais cara', cls: 'callout--success'} :
                                                            cmp === 'igual' ? {txt: 'Mesmo preço', cls: 'callout--info'} :
                                                            null
                                                        const linkPncp = linkPncpPorControle.get(s.numero_controle)
                                                        const tituloConferir = 'O item exato cotejado não pôde ser confirmado automaticamente — confira antes de decidir.'
                                                        return (
                                                            <div
                                                                key={s.numero_controle}
                                                                style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border-subtle)'}}
                                                            >
                                                                <div style={{flex: 1, minWidth: 200}}>
                                                                    <div style={{fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)' as any, color: 'var(--text-primary)'}}>
                                                                        {rotuloNumeroAta(s.numero_ata || s.numero_controle)}{s.orgao ? ` — ${s.orgao}` : ''}{s.uf ? ` (${s.uf})` : ''}
                                                                    </div>
                                                                    <div style={{fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)'}}>
                                                                        {naoLocalizado
                                                                            ? 'Item correspondente não localizado nesta ata'
                                                                            : it && (valor || it.descricao)
                                                                            ? <>{valor && <strong>{valor}</strong>}{valor && it.descricao ? ' · ' : ''}{it.descricao || ''}</>
                                                                            : 'sem preço comparável'}
                                                                        {!naoLocalizado && s.item_comparado_incerto && (
                                                                            linkPncp ? (
                                                                                <a
                                                                                    href={linkPncp}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    title={tituloConferir}
                                                                                    style={{marginLeft: 6, color: 'var(--feedback-warning)', whiteSpace: 'nowrap', textDecoration: 'underline', cursor: 'pointer'}}
                                                                                >
                                                                                    · conferir item
                                                                                </a>
                                                                            ) : onAbrirDetalhe ? (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => onAbrirDetalhe(s.numero_controle)}
                                                                                    title={tituloConferir}
                                                                                    style={{marginLeft: 6, color: 'var(--feedback-warning)', whiteSpace: 'nowrap', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit'}}
                                                                                >
                                                                                    · conferir item
                                                                                </button>
                                                                            ) : (
                                                                                <span
                                                                                    title={tituloConferir}
                                                                                    style={{marginLeft: 6, color: 'var(--feedback-warning)', whiteSpace: 'nowrap'}}
                                                                                >
                                                                                    · conferir item
                                                                                </span>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {selo && (
                                                                    <span className={`callout ${selo.cls}`} style={{margin: 0, padding: '2px 8px', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-medium)' as any}}>
                                                                        {selo.txt}
                                                                    </span>
                                                                )}
                                                                {cmp === 'barata' && onAdotarAta && (
                                                                    <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={() => adotarAlternativa(s)}>
                                                                        Adotar esta ata
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                            <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', margin: '8px 0 0'}}>
                                                Comparação por preço unitário na mesma unidade-base (item exato de cada ata). A Licito.Guru IA é apoio — confira os dados antes de decidir.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div style={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, margin: '10px 0 2px'}}>
                                <button
                                    type="button"
                                    className="lg-btn lg-btn--brand lg-btn--sm"
                                    onClick={sugerirJustificativas}
                                    disabled={sugerindoAlt}
                                    aria-busy={sugerindoAlt}
                                    title={`Consulta a Licito.Guru IA e preenche as razões — você revisa e ajusta (pode levar até ${TIMEOUT_SEGUNDOS}s).`}
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z"/></svg>
                                    {sugerindoAlt ? 'Gerando sugestões (Licito.Guru IA)…' : 'Sugerir justificativas (Licito.Guru IA)'}
                                </button>
                                <span style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)'}}>
                                    Preenche cada razão com uma sugestão da Licito.Guru IA — os campos continuam editáveis (você revisa e ajusta).
                                </span>
                            </div>
                            {sugerindoAlt && (
                                <ProgressoIA segundos={segundosSugestoes} rotulo="Gerando sugestões" />
                            )}
                            {erroSugestoes && !sugerindoAlt && (
                                <div className="callout callout--danger" role="alert" style={{margin: '10px 0 0'}}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                    <div>{erroSugestoes}</div>
                                </div>
                            )}
                            {consideradas.map((row, i) => (
                                <div key={i} style={{display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'start', margin: '10px 0'}}>
                                    <input
                                        className="lg-input" type="text" value={row.referencia}
                                        aria-label={`Alternativa considerada ${i + 1}`}
                                        onChange={(ev) => updateAlt(i, {referencia: ev.target.value})}
                                        placeholder="Alternativa (ex.: Ata 050/2025 — Órgão Y, ou licitação própria)"
                                    />
                                    <input
                                        className="lg-input" type="text" value={row.motivo}
                                        aria-label={`Motivo da alternativa ${i + 1}`}
                                        onChange={(ev) => updateAlt(i, {motivo: ev.target.value})}
                                        placeholder="Por que não foi a escolhida / por que a escolhida é melhor"
                                    />
                                    <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={() => removeAlt(i)} aria-label={`Remover alternativa ${i + 1}`}>
                                        Remover
                                    </button>
                                </div>
                            ))}
                            <button type="button" className="lg-btn lg-btn--secondary lg-btn--sm" onClick={addAlt}>
                                + Adicionar alternativa
                            </button>
                            <div className="lg-field" style={{marginTop: 12}}>
                                <label className="lg-label">Observações sobre a análise de alternativas (opcional)</label>
                                <textarea
                                    className="lg-textarea" value={e.alternativas_observacao || ''}
                                    onChange={(ev) => set({alternativas_observacao: ev.target.value})}
                                    placeholder="Ex.: a adesão evita novo processo licitatório e antecipa a entrega; os preços serão confirmados por pesquisa própria (art. 23)."
                                />
                            </div>
                        </Bloco>
                    </div>

                    <div>
                        <div className="conformidade">
                            <div className="conformidade__head">
                                <div className="conformidade__title">Painel de conformidade</div>
                                <div className="conformidade__sub">Checklist legal — prévia (verificação final ao gerar a justificativa)</div>
                                <div className="progress-ring">
                                    <div className="progress-ring__bar"><div className="progress-ring__fill" style={{width: `${pct}%`}}></div></div>
                                    <div className="progress-ring__count">{ok}/{total}</div>
                                </div>
                            </div>
                            <ul className="req-list">
                                {reqs.map((r) => (
                                    <li key={r.id} className={`req-item req-item--${r.status}`}>
                                        <span className="req-item__icon">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                                                dangerouslySetInnerHTML={{__html: REQ_ICON[r.status]}} />
                                        </span>
                                        <div><div className="req-item__txt">{r.titulo}</div><div className="req-item__hint">{r.base_legal} · {r.hint}</div></div>
                                    </li>
                                ))}
                            </ul>
                            <div style={{padding: '14px 16px', borderTop: '1px solid var(--border-subtle)'}}>
                                {(() => {
                                    // Fluidez entre módulos (pedido do dono): com Pesquisa de Preços
                                    // vinculada EM ANDAMENTO, a redação espera — o orçamento estimado é
                                    // insumo da fundamentação e entra sozinho quando a pesquisa conclui.
                                    // status null = "aguardando dados" (vinculada) → também segura.
                                    // ERRO na pesquisa → libera (não tranca o gestor por falha alheia).
                                    const aguardandoPesquisa = !!pesquisaVinculada
                                        && !pesquisaVinculada.concluida
                                        && pesquisaVinculada.status !== 'ERRO'
                                    return <>
                                        <button className="lg-btn lg-btn--brand lg-btn--block" disabled={gerando || aguardandoPesquisa} onClick={onGerar}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                            {aguardandoPesquisa ? 'Aguardando a Pesquisa de Preços…' : gerando ? 'Gerando…' : 'Gerar justificativa (1 documento)'}
                                        </button>
                                        <p style={{fontSize: 'var(--fs-xs)', color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 8}}>
                                            {aguardandoPesquisa
                                                ? 'A Pesquisa de Preços vinculada está em andamento — a redação libera automaticamente quando ela concluir, já com o orçamento estimado na fundamentação.'
                                                : 'Gera mesmo com pendências — em modo minuta marcada.'}
                                        </p>
                                    </>
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
